import { IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  QUERY_PROGRESS_PENDING,
  TRACK_EVENTS,
} from "core/consts";
import { FileHISStateful, Filev2, QueryProgress } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { padding } from "ds_legacy/materials/metrics";
import { FONT_SIZE_20 } from "ds_legacy/materials/typography";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { FileUpIcon, MonitorIcon, XIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { useDialogStyles } from "..";
import FilesTableHIS from "./FilesTableHIS";

type DialogTitleProps = {
  children?: ReactNode;
  id: string;
  onClose: () => void;
};

function EnhancedDialogTitle({ children, onClose }: DialogTitleProps) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  return (
    <DialogTitle sx={{ fontSize: FONT_SIZE_20, padding: padding(2.5, 4.5, 0) }}>
      {children}
      <IconButton
        aria-label={translations.actions.close}
        sx={{
          color: (theme) => theme.palette.grey[500],
          position: "absolute",
          right: 8,
          top: 8,
        }}
        onClick={() => {
          trackEvent({
            cta: "x",
            file_implementation: "HIS",
            name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
          });
          onClose();
        }}
      >
        <XIcon size={16} />
      </IconButton>
    </DialogTitle>
  );
}

export function FileUploadHISModal({
  availableFiles,
  closeModal,
  getFilesHISProgress,
  isOpen,
  mutationProgress,
  setAction,
  unavailableFiles,
}: {
  availableFiles: FileHISStateful[] | null;
  closeModal: () => void;
  getFilesHISProgress: QueryProgress;
  isOpen?: boolean;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
}) {
  const [filesSelected, setFilesSelected] = useState<FileHISStateful[]>([]);
  const classes = useDialogStyles({ size: "medium" });
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  function handleClose() {
    setFilesSelected([]);
    closeModal();
  }

  function handleCancel() {
    trackEvent({
      cta: "cancel",
      file_implementation: "HIS",
      name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
    });
    handleClose();
  }

  function handleClickSelectFromComputer() {
    trackEvent({
      file_implementation: "HIS",
      name: TRACK_EVENTS.SELECT_FROM_COMPUTER_CLICKED,
    });
    setAction({
      data: {} as Filev2,
      type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
    });
  }

  function handleUploadFile() {
    trackEvent({
      file_implementation: "HIS",
      name: TRACK_EVENTS.UPLOAD_FILE_CLICKED,
    });
    setAction({
      data: filesSelected,
      type: FILE_TABLE_ACTION_UPLOAD_HIS,
    });
    setFilesSelected([]);
  }

  if (!isOpen) return null;

  return (
    <Dialog
      classes={{ paper: classes.dialog }}
      open={isOpen}
      onClose={() => {
        trackEvent({
          cta: "outside_modal",
          file_implementation: "HIS",
          name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
        });
        handleClose();
      }}
    >
      <EnhancedDialogTitle id="customized-dialog-title" onClose={handleClose}>
        {translations.fileHisUploadComponent.title}
      </EnhancedDialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <FilesTableHIS
            data={availableFiles}
            description={
              translations.fileSection.fileListComponent
                .descriptionFilesAvailable
            }
            filesSelected={filesSelected}
            queryProgress={getFilesHISProgress}
            setFilesSelected={setFilesSelected}
            title={
              translations.fileSection.fileListComponent.titleFilesAvailable
            }
          />
          <FilesTableHIS
            data={unavailableFiles}
            description={
              translations.fileSection.fileListComponent
                .descriptionFilesUnsupported
            }
            filesSelected={null}
            queryProgress={getFilesHISProgress}
            setFilesSelected={null}
            title={
              translations.fileSection.fileListComponent.titleFilesUnavailable
            }
          />
        </div>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: "space-between", padding: padding(2, 4) }}
      >
        <div>
          <RSButton
            id="select_file_from_computer"
            LeftIcon={MonitorIcon}
            loading="na"
            variant="outlined"
            onClick={handleClickSelectFromComputer}
          >
            {translations.actions.selectFileFromComputer}
          </RSButton>
        </div>
        <div style={{ display: "flex" }}>
          <RSButton
            color="primary"
            disabled={mutationProgress === QUERY_PROGRESS_PENDING}
            id="cancel"
            loading="na"
            variant="text"
            onClick={handleCancel}
          >
            {translations.actions.cancel}
          </RSButton>
          <RSButton
            color="primary"
            disabled={!filesSelected.length}
            id="upload_his"
            LeftIcon={FileUpIcon}
            loading={mutationProgress}
            variant="contained"
            onClick={handleUploadFile}
          >
            {translations.fileSection.fileUploadComponent.uploadFile}
          </RSButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
export default FileUploadHISModal;
